import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { patientService, consultationService, facturationService, planningService } from '../../services/api';
import { Patient, Consultation, Facture, RendezVous, PaginatedResponse } from '../../types';
import DiagrammeDentaire from '../consultations/DiagrammeDentaire';
import {
  ArrowLeft,
  User,
  Calendar,
  FileText,
  CreditCard,
  Phone,
  Mail,
  MapPin,
  Heart,
  AlertTriangle,
  Pill,
  UserCheck,
  Clock,
  Plus,
  CalendarPlus,
  Printer,
  Download,
  Eye,
  Wallet
} from 'lucide-react';
import { downloadPDF, openPDFInNewTab, handlePrintError } from '../../utils/pdfUtils';
import ConsultationForm from '../consultations/ConsultationForm';
import FactureForm from '../facturation/FactureForm';
import CompteClientTab from './CompteClientTab';

interface PatientDetailProps {
  patientId?: number;
  onClose?: () => void;
}

const PatientDetail: React.FC<PatientDetailProps> = ({ patientId: propPatientId, onClose }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'info' | 'consultations' | 'factures' | 'compte' | 'rendez-vous'>('info');
  const [showConsultationForm, setShowConsultationForm] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState<Consultation | null>(null);
  const [showFactureForm, setShowFactureForm] = useState(false);
  const [selectedFacture, setSelectedFacture] = useState<Facture | null>(null);

  const patientId = propPatientId || (id ? parseInt(id) : 0);

  // Récupération des données du patient
  const { data: patient, isLoading: patientLoading } = useQuery<Patient>({
    queryKey: ['patient', patientId],
    queryFn: () => patientService.getById(patientId).then(res => res.data),
    enabled: !!patientId
  });

  // Récupération des consultations
  const { data: consultationsResponse } = useQuery<PaginatedResponse<Consultation>>({
    queryKey: ['consultations', 'patient', patientId],
    queryFn: () => consultationService.getAll({ patient: patientId, page_size: 100 }).then(res => res.data),
    enabled: !!patientId
  });
  const consultations = consultationsResponse?.results || [];

  // Récupération des factures
  const { data: facturesResponse } = useQuery<PaginatedResponse<Facture>>({
    queryKey: ['factures', 'patient', patientId],
    queryFn: () => facturationService.getFactures({ patient: patientId, page_size: 100 }).then(res => res.data),
    enabled: !!patientId
  });
  const factures = facturesResponse?.results || [];

  // Récupération des rendez-vous
  const { data: rendezVousResponse } = useQuery<PaginatedResponse<RendezVous>>({
    queryKey: ['rendez-vous', 'patient', patientId],
    queryFn: () => planningService.getRendezVous({ patient: patientId, page_size: 100 }).then(res => res.data),
    enabled: !!patientId
  });
  const rendezVous = rendezVousResponse?.results || [];

  const handleBack = () => {
    if (onClose) {
      onClose();
    } else {
      navigate('/patients');
    }
  };

  const handleCreateConsultation = () => {
    navigate('/consultations', {
      state: {
        patientId: patient?.id,
        patientInfo: {
          nom: patient?.nom,
          prenoms: patient?.prenoms,
          telephone: patient?.telephone
        }
      }
    });
  };

  const handleScheduleAppointment = () => {
    navigate('/planning', {
      state: {
        patientId: patient?.id,
        patientInfo: {
          nom: patient?.nom,
          prenoms: patient?.prenoms,
          telephone: patient?.telephone
        }
      }
    });
  };

  const handlePrintOrdonnance = async (consultationId: number) => {
    try {
      const response = await consultationService.imprimerOrdonnance(consultationId);
      openPDFInNewTab(response.data);
    } catch (error) {
      handlePrintError(error, 'ordonnance');
    }
  };

  const handlePrintFacture = async (factureId: number) => {
    try {
      const response = await facturationService.imprimerFacture(factureId);
      openPDFInNewTab(response.data);
    } catch (error) {
      handlePrintError(error, 'facture');
    }
  };

  const handleOpenFactures = () => {
    setActiveTab('factures');
  };

  const handleOpenConsultation = (consultation: Consultation) => {
    setSelectedConsultation(consultation);
    setShowConsultationForm(true);
  };

  const handleCloseConsultationForm = () => {
    setShowConsultationForm(false);
    setSelectedConsultation(null);
  };

  const handleConsultationSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['consultations', 'patient', patientId] });
    handleCloseConsultationForm();
  };

  const handleOpenFacture = (facture: Facture) => {
    setSelectedFacture(facture);
    setShowFactureForm(true);
  };

  const handleCloseFactureForm = () => {
    setShowFactureForm(false);
    setSelectedFacture(null);
  };

  const handleFactureSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['factures', 'patient', patientId] });
    handleCloseFactureForm();
  };

  if (patientLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Patient introuvable</p>
        <button
          onClick={handleBack}
          className="mt-4 px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700"
        >
          Retour
        </button>
      </div>
    );
  }

  const tabs = [
    { id: 'info', label: 'Informations', icon: User },
    { id: 'consultations', label: 'Consultations', icon: FileText, count: consultations.length },
    { id: 'factures', label: 'Factures', icon: CreditCard, count: factures.length },
    { id: 'compte', label: 'Compte client', icon: Wallet },
    { id: 'rendez-vous', label: 'Rendez-vous', icon: Calendar, count: rendezVous.length }
  ];

  return (
    <div className="max-w-7xl mx-auto son-panel">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleBack}
              className="flex items-center text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Retour
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {patient.nom} {patient.prenoms}
              </h1>
              <p className="text-gray-600">
                {patient.age} ans • {patient.genre === 'M' ? 'Masculin' : 'Féminin'}
              {factures.length > 0 && (
                <button
                  onClick={handleOpenFactures}
                  className="ml-4 text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  {factures.length} facture{factures.length > 1 ? 's' : ''}
                </button>
              )}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleCreateConsultation}
              className="flex items-center px-3 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 text-sm"
            >
              <Plus className="h-4 w-4 mr-1" />
              Consultation
            </button>
            <button
              onClick={handleScheduleAppointment}
              className="flex items-center px-3 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 text-sm"
            >
              <CalendarPlus className="h-4 w-4 mr-1" />
              RDV
            </button>
            {patient.assureur_details && (
              <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                <UserCheck className="h-4 w-4 inline mr-1" />
                Assuré
              </span>
            )}
            <span className={`px-2 py-1 rounded-full text-sm ${
              patient.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {patient.is_active ? 'Actif' : 'Inactif'}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 px-6">
          {tabs.map((tab) => {
            const IconComponent = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <IconComponent className="h-4 w-4" />
                <span>{tab.label}</span>
                {tab.count !== undefined && (
                  <span className="bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'info' && (
          <PatientInfoTab patient={patient} />
        )}
        {activeTab === 'consultations' && (
          <ConsultationsTab
            consultations={consultations}
            onPrintOrdonnance={handlePrintOrdonnance}
            onOpenConsultation={handleOpenConsultation}
          />
        )}
        {activeTab === 'factures' && (
          <FacturesTab
            factures={factures}
            onPrintFacture={handlePrintFacture}
            onOpenFacture={handleOpenFacture}
          />
        )}
        {activeTab === 'compte' && (
          <CompteClientTab patientId={patientId} />
        )}
        {activeTab === 'rendez-vous' && (
          <RendezVousTab rendezVous={rendezVous} />
        )}
      </div>

      {/* Modal pour consultation */}
      {showConsultationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <ConsultationForm
                consultation={selectedConsultation || undefined}
                onClose={handleCloseConsultationForm}
                onSuccess={handleConsultationSuccess}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal pour facture */}
      {showFactureForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <FactureForm
                facture={selectedFacture || undefined}
                onClose={handleCloseFactureForm}
                onSuccess={handleFactureSuccess}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Composant pour l'onglet informations
const PatientInfoTab: React.FC<{ patient: Patient }> = ({ patient }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Informations personnelles */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <User className="h-5 w-5 mr-2" />
          Informations personnelles
        </h3>
        <div className="space-y-3">
          <div className="flex items-center">
            <Phone className="h-4 w-4 text-gray-400 mr-3" />
            <span className="text-gray-700">{patient.telephone || 'Non renseigné'}</span>
          </div>
          <div className="flex items-center">
            <Mail className="h-4 w-4 text-gray-400 mr-3" />
            <span className="text-gray-700">{patient.email || 'Non renseigné'}</span>
          </div>
          <div className="flex items-start">
            <MapPin className="h-4 w-4 text-gray-400 mr-3 mt-0.5" />
            <span className="text-gray-700">{patient.adresse || 'Non renseigné'}</span>
          </div>
          <div className="flex items-center">
            <Calendar className="h-4 w-4 text-gray-400 mr-3" />
            <span className="text-gray-700">
              Né le {new Date(patient.date_naissance).toLocaleDateString('fr-FR')}
            </span>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-500">Profession:</span>
            <span className="ml-2 text-gray-700">{patient.profession || 'Non renseigné'}</span>
          </div>
        </div>
      </div>

      {/* Informations médicales */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <Heart className="h-5 w-5 mr-2" />
          Informations médicales
        </h3>
        <div className="space-y-4">
          <div>
            <div className="flex items-center mb-2">
              <Heart className="h-4 w-4 text-red-400 mr-2" />
              <span className="font-medium text-gray-700">Antécédents médicaux</span>
            </div>
            <p className="text-gray-600 text-sm ml-6">
              {patient.antecedents_medicaux || 'Aucun antécédent renseigné'}
            </p>
          </div>
          <div>
            <div className="flex items-center mb-2">
              <AlertTriangle className="h-4 w-4 text-yellow-400 mr-2" />
              <span className="font-medium text-gray-700">Allergies</span>
            </div>
            <p className="text-gray-600 text-sm ml-6">
              {patient.allergies || 'Aucune allergie connue'}
            </p>
          </div>
          <div>
            <div className="flex items-center mb-2">
              <Pill className="h-4 w-4 text-blue-400 mr-2" />
              <span className="font-medium text-gray-700">Médicaments actuels</span>
            </div>
            <p className="text-gray-600 text-sm ml-6">
              {patient.medicaments_actuels || 'Aucun médicament en cours'}
            </p>
          </div>
        </div>
      </div>

      {/* Assurance */}
      {patient.assureur_details && (
        <div className="bg-gray-50 rounded-lg p-6 lg:col-span-2">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <UserCheck className="h-5 w-5 mr-2" />
            Assurance
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-sm font-medium text-gray-500">Assureur:</span>
              <span className="ml-2 text-gray-700">{patient.assureur_details.nom}</span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">Numéro d'assuré:</span>
              <span className="ml-2 text-gray-700">{patient.numero_assure || 'Non renseigné'}</span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">Souscripteur:</span>
              <span className="ml-2 text-gray-700">{patient.nom_souscripteur || 'Non renseigné'}</span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">Contact assureur:</span>
              <span className="ml-2 text-gray-700">{patient.assureur_details.contact || 'Non renseigné'}</span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">Email assureur:</span>
              <span className="ml-2 text-gray-700">{patient.assureur_details.email || 'Non renseigné'}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Composant pour l'onglet consultations
const ConsultationsTab: React.FC<{
  consultations: Consultation[];
  onPrintOrdonnance: (id: number) => void;
  onOpenConsultation: (consultation: Consultation) => void;
}> = ({ consultations, onPrintOrdonnance, onOpenConsultation }) => {
  if (consultations.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
        <p>Aucune consultation enregistrée</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        Historique des consultations ({consultations.length})
      </h3>
      {consultations.map((consultation) => (
        <div key={consultation.id} className="bg-gray-50 rounded-lg p-4 border-l-4 border-blue-500">
          <div className="flex justify-between items-start mb-3">
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span className="font-medium text-gray-800">
                  {new Date(consultation.date_consultation).toLocaleDateString('fr-FR')}
                </span>
                <span className="text-gray-500">
                  {new Date(consultation.date_consultation).toLocaleTimeString('fr-FR', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </span>
              </div>
              <div className="text-sm text-gray-600">
                Médecin: {consultation.doctor_details?.user_details ? (
                  `Dr. ${consultation.doctor_details.user_details.first_name} ${consultation.doctor_details.user_details.last_name}`
                ) : 'Non assigné'}
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => onOpenConsultation(consultation)}
                className="flex items-center px-3 py-1 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors"
                title="Voir détails"
              >
                <Eye className="h-4 w-4 mr-1" />
                Voir
              </button>
              <button
                onClick={() => onPrintOrdonnance(consultation.id)}
                className="flex items-center px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                title="Imprimer ordonnance"
              >
                <Printer className="h-4 w-4 mr-1" />
                Ordonnance
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-700">Motif:</span>
              <p className="text-gray-600 mt-1">{consultation.motif_consultation}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Diagnostic:</span>
              <p className="text-gray-600 mt-1">{consultation.diagnostic || 'Non renseigné'}</p>
            </div>
          </div>

          {consultation.treatments && consultation.treatments.length > 0 && (
            <div className="mt-3">
              <span className="font-medium text-gray-700 text-sm">Traitements réalisés:</span>
              <div className="mt-1 space-y-1">
                {consultation.treatments.map((treatment, index) => (
                  <div key={index} className="text-sm text-gray-600 flex justify-between">
                    <span>{treatment.treatment_details?.name || 'Traitement'} (x{treatment.quantite})</span>
                    <span>{(treatment.quantite * treatment.prix_unitaire).toLocaleString()} GNF</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {consultation.dents_concernees && (
            <div className="mt-2">
              <span className="font-medium text-gray-700 text-sm">Dents concernées:</span>
              <span className="ml-2 text-gray-600 text-sm">{consultation.dents_concernees}</span>
            </div>
          )}

          {consultation.odontogramme &&
            (Object.keys(consultation.odontogramme.teeth || {}).length > 0 ||
              (consultation.odontogramme.bridges || []).length > 0) && (
              <details className="mt-3">
                <summary className="text-sm font-medium text-blue-600 cursor-pointer hover:text-blue-800">
                  Afficher l'odontogramme
                </summary>
                <div className="mt-2">
                  <DiagrammeDentaire
                    value={consultation.odontogramme}
                    onChange={() => {}}
                    patientAge={consultation.patient_details?.age}
                    readOnly
                  />
                </div>
              </details>
            )}
        </div>
      ))}
    </div>
  );
};

// Composant pour l'onglet factures
const FacturesTab: React.FC<{
  factures: Facture[];
  onPrintFacture: (id: number) => void;
  onOpenFacture: (facture: Facture) => void;
}> = ({ factures, onPrintFacture, onOpenFacture }) => {
  if (factures.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <CreditCard className="h-12 w-12 mx-auto mb-4 text-gray-300" />
        <p>Aucune facture enregistrée</p>
      </div>
    );
  }

  const totalFacture = factures.reduce((sum, f) => sum + Number(f.montant_total), 0);
  const totalPaye = factures.reduce((sum, f) => {
    return sum + (f.paiements?.reduce((pSum, p) => pSum + Number(p.montant), 0) || 0);
  }, 0);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-800">
          Factures et paiements ({factures.length})
        </h3>
        <div className="text-right text-sm">
          <div className="text-gray-600">Total facturé: <span className="font-semibold">{totalFacture.toLocaleString()} GNF</span></div>
          <div className="text-gray-600">Total payé: <span className="font-semibold">{totalPaye.toLocaleString()} GNF</span></div>
          <div className={`font-semibold ${totalFacture - totalPaye > 0 ? 'text-red-600' : 'text-green-600'}`}>
            Restant: {(totalFacture - totalPaye).toLocaleString()} GNF
          </div>
        </div>
      </div>

      {factures.map((facture) => {
        const montantPaye = facture.paiements?.reduce((sum, p) => sum + Number(p.montant), 0) || 0;
        const montantRestant = Number(facture.montant_patient) - montantPaye;

        // Fonction pour obtenir la couleur du statut (alignée avec FacturationList)
        const getStatusColor = (status: string): string => {
          const colors: Record<string, string> = {
            'brouillon': 'bg-gray-100 text-gray-800',
            'emise': 'bg-blue-100 text-blue-800',
            'payee': 'bg-green-100 text-green-800',
            'partiellement_payee': 'bg-yellow-100 text-yellow-800',
            'prise_en_charge_assurance': 'bg-purple-100 text-purple-800',
            'annulee': 'bg-red-100 text-red-800'
          };
          return colors[status] || 'bg-gray-100 text-gray-800';
        };

        // Fonction pour obtenir le libellé du statut (alignée avec FacturationList)
        const getStatusLabel = (status: string): string => {
          const labels: Record<string, string> = {
            'brouillon': 'Brouillon',
            'emise': 'Émise',
            'payee': 'Payée',
            'partiellement_payee': 'Partiellement payée',
            'prise_en_charge_assurance': 'Prise en charge assurance',
            'annulee': 'Annulée'
          };
          return labels[status] || status;
        };

        return (
          <div key={facture.id} className="bg-gray-50 rounded-lg p-4 border-l-4 border-green-500">
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="flex items-center space-x-2 mb-1">
                  <span className="font-medium text-gray-800">{facture.numero}</span>
                  <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(facture.status)}`}>
                    {getStatusLabel(facture.status)}
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  Émise le {new Date(facture.date_emission).toLocaleDateString('fr-FR')}
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => onOpenFacture(facture)}
                  className="flex items-center px-3 py-1 text-sm bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors"
                  title="Voir détails"
                >
                  <Eye className="h-4 w-4 mr-1" />
                  Voir
                </button>
                <button
                  onClick={() => onPrintFacture(facture.id)}
                  className="flex items-center px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600 transition-colors"
                  title="Imprimer facture"
                >
                  <Printer className="h-4 w-4 mr-1" />
                  Imprimer
                </button>
                <div className="text-right">
                  <div className="font-semibold text-gray-800">{Number(facture.montant_total).toLocaleString()} GNF</div>
                  <div className="text-sm text-gray-600">Total</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Montant patient:</span>
                <div className="text-gray-600">{Number(facture.montant_patient).toLocaleString()} GNF</div>
              </div>
              <div>
                <span className="font-medium text-gray-700">Montant payé:</span>
                <div className="text-gray-600">{montantPaye.toLocaleString()} GNF</div>
              </div>
              <div>
                <span className="font-medium text-gray-700">Restant:</span>
                <div className={`font-semibold ${montantRestant > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {montantRestant.toLocaleString()} GNF
                </div>
              </div>
            </div>

            {facture.paiements && facture.paiements.length > 0 && (
              <div className="mt-3">
                <span className="font-medium text-gray-700 text-sm">Paiements:</span>
                <div className="mt-1 space-y-1">
                  {facture.paiements.map((paiement) => (
                    <div key={paiement.id} className="text-sm text-gray-600 flex justify-between">
                      <span>
                        {new Date(paiement.date_paiement).toLocaleDateString('fr-FR')} - {paiement.mode_paiement}
                      </span>
                      <span>{Number(paiement.montant).toLocaleString()} GNF</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Composant pour l'onglet rendez-vous
const RendezVousTab: React.FC<{ rendezVous: RendezVous[] }> = ({ rendezVous }) => {
  const navigate = useNavigate();

  const voirDansCalendrier = (rdv: RendezVous) => {
    navigate('/planning', { state: { targetDate: rdv.date_heure } });
  };

  if (rendezVous.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
        <p>Aucun rendez-vous enregistré</p>
      </div>
    );
  }

  const prochains = rendezVous.filter(rdv => new Date(rdv.date_heure) > new Date());
  const passes = rendezVous.filter(rdv => new Date(rdv.date_heure) <= new Date());

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-800">
        Rendez-vous ({rendezVous.length})
      </h3>

      {prochains.length > 0 && (
        <div>
          <h4 className="font-medium text-gray-700 mb-3 flex items-center">
            <Clock className="h-4 w-4 mr-2" />
            Prochains rendez-vous ({prochains.length})
          </h4>
          <div className="space-y-3">
            {prochains.map((rdv) => (
              <div key={rdv.id} className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center space-x-2 mb-1">
                      <Calendar className="h-4 w-4 text-blue-500" />
                      <span className="font-medium text-gray-800">
                        {new Date(rdv.date_heure).toLocaleDateString('fr-FR')}
                      </span>
                      <span className="text-gray-600">
                        à {new Date(rdv.date_heure).toLocaleTimeString('fr-FR', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      Médecin: {rdv.doctor_details?.user_details ? (
                        `Dr. ${rdv.doctor_details.user_details.first_name} ${rdv.doctor_details.user_details.last_name}`
                      ) : 'Non assigné'}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      <span className="font-medium">Motif:</span> {rdv.motif}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      rdv.status === 'confirme' ? 'bg-green-100 text-green-800' :
                      rdv.status === 'attente' ? 'bg-yellow-100 text-yellow-800' :
                      rdv.status === 'annule' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {rdv.status === 'confirme' ? 'Confirmé' :
                       rdv.status === 'attente' ? 'En attente' :
                       rdv.status === 'annule' ? 'Annulé' :
                       rdv.status === 'realise' ? 'Réalisé' : 'Non honoré'}
                    </span>
                    <button
                      onClick={() => voirDansCalendrier(rdv)}
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                      title="Voir ce RDV dans le planning"
                    >
                      <Calendar className="h-3 w-3" />
                      Voir dans le planning
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {passes.length > 0 && (
        <div>
          <h4 className="font-medium text-gray-700 mb-3">
            Historique ({passes.length})
          </h4>
          <div className="space-y-3">
            {passes.map((rdv) => (
              <div key={rdv.id} className="bg-gray-50 rounded-lg p-4 border-l-4 border-gray-300">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center space-x-2 mb-1">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="font-medium text-gray-800">
                        {new Date(rdv.date_heure).toLocaleDateString('fr-FR')}
                      </span>
                      <span className="text-gray-600">
                        à {new Date(rdv.date_heure).toLocaleTimeString('fr-FR', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      Médecin: {rdv.doctor_details?.user_details ? (
                        `Dr. ${rdv.doctor_details.user_details.first_name} ${rdv.doctor_details.user_details.last_name}`
                      ) : 'Non assigné'}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      <span className="font-medium">Motif:</span> {rdv.motif}
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    rdv.status === 'realise' ? 'bg-green-100 text-green-800' :
                    rdv.status === 'annule' ? 'bg-red-100 text-red-800' :
                    rdv.status === 'non_honore' ? 'bg-orange-100 text-orange-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {rdv.status === 'realise' ? 'Réalisé' :
                     rdv.status === 'annule' ? 'Annulé' :
                     rdv.status === 'non_honore' ? 'Non honoré' : 'Confirmé'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientDetail;